import React, { useEffect, useState } from 'react';
import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
} from 'recharts';

import { responses } from 'services/api';
import { LoadingAnimation } from 'components/ui/loading-animation';
import { useResponsiveLayout } from 'hooks/useResponsiveLayout';

export const DEFAULT_RADAR_CHART_DEFINITION_KEY = 'intake-pillars';

export interface RadarAxisData {
  key: string;
  label: string;
  order: number;
  initial_value: number | null;
  latest_value: number | null;
  last_updated_at: string | null;
  source_question_count: number;
}

export interface RadarChartData {
  definition: {
    key: string;
    name: string;
    axis_count: number;
    scale_min: number;
    scale_max: number;
    recent_response_count: number;
  };
  axes: RadarAxisData[];
  is_complete: boolean;
}

const PARTIAL_DATA_RETRY_DELAY_MS = 750;
const MAX_PARTIAL_DATA_RETRIES = 2;

const MOBILE_AXIS_LINES: Record<string, string[]> = {
  'Community & Connection': ['Community &', 'Connection'],
  'Sleep Rest & Recovery': ['Sleep &', 'Recovery'],
  'Sleep, Rest, Recovery': ['Sleep &', 'Recovery'],
  'Physical Fitness': ['Physical', 'Fitness'],
  'Emotional Fitness': ['Emotional', 'Fitness'],
  'Mental Health Fitness': ['Mental', 'Health'],
  'Financial Fitness': ['Financial', 'Fitness'],
};

interface CustomRadarChartProps {
  definitionKey: string;
  onDataLoaded?: (data: RadarChartData) => void;
  retryUntilComplete?: boolean;
}

export const CustomRadarChart: React.FC<CustomRadarChartProps> = ({
  definitionKey,
  onDataLoaded,
  retryUntilComplete = false,
}) => {
  const { isMobile } = useResponsiveLayout();
  const [data, setData] = useState<RadarChartData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shouldRenderChart, setShouldRenderChart] = useState(false);

  useEffect(() => {
    const timeout = window.setTimeout(() => setShouldRenderChart(true), 150);
    return () => {
      window.clearTimeout(timeout);
      setShouldRenderChart(false);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const retryTimeouts: number[] = [];
    setLoading(true);
    setError(null);
    setData(null);

    const fetchData = async (attempt = 0) => {
      let scheduledRetry = false;
      try {
        const response = await responses.getRadarChartData(definitionKey);
        const responseData = response.data as RadarChartData;
        const shouldRetry =
          retryUntilComplete &&
          responseData.is_complete !== true &&
          attempt < MAX_PARTIAL_DATA_RETRIES;

        if (shouldRetry) {
          scheduledRetry = true;
          const timeout = window.setTimeout(
            () => fetchData(attempt + 1),
            PARTIAL_DATA_RETRY_DELAY_MS,
          );
          retryTimeouts.push(timeout);
          return;
        }

        if (!isMounted) {
          return;
        }

        setData(responseData);
        onDataLoaded?.(responseData);
      } catch (err) {
        console.error('Error fetching radar chart data:', err);
        if (isMounted) {
          setError('Failed to load progress data');
        }
      } finally {
        if (isMounted && !scheduledRetry) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      retryTimeouts.forEach((timeout) => window.clearTimeout(timeout));
    };
  }, [definitionKey, onDataLoaded, retryUntilComplete]);

  if (loading) {
    return (
      <div className="flex h-80 w-full items-center justify-center">
        <LoadingAnimation size="sm" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex h-80 w-full items-center justify-center text-sm text-destructive">
        {error || 'No data available'}
      </div>
    );
  }

  const chartData = data.axes
    .filter((axis): axis is RadarAxisData & { latest_value: number } => axis.latest_value !== null)
    .map((axis) => ({
      category: axis.label,
      value: axis.latest_value,
    }));

  if (chartData.length === 0) {
    return (
      <div className="flex h-80 w-full items-center justify-center text-sm text-muted-foreground">
        No progress data available
      </div>
    );
  }

  if (!shouldRenderChart) {
    return (
      <div className="flex h-80 min-h-[20rem] w-full min-w-0 items-center justify-center">
        <LoadingAnimation size="sm" />
      </div>
    );
  }

  const primary = 'var(--foundation-purplepurple-500)';
  const latestResponseAt = data.axes.reduce<string | null>((latest, axis) => {
    if (!axis.last_updated_at) {
      return latest;
    }
    if (!latest || new Date(axis.last_updated_at) > new Date(latest)) {
      return axis.last_updated_at;
    }
    return latest;
  }, null);

  return (
    <div className="w-full min-w-0">
      <div className="h-[17rem] min-h-[17rem] w-full min-w-0 sm:h-80 sm:min-h-[20rem]">
        <ResponsiveContainer width="100%" height="100%" debounce={200}>
          <RadarChart
            cx="50%"
            cy="50%"
            outerRadius={isMobile ? '48%' : '80%'}
            data={chartData}
            margin={isMobile
              ? { top: 16, right: 12, bottom: 16, left: 12 }
              : { top: 20, right: 20, bottom: 20, left: 20 }}
          >
          <PolarGrid stroke={primary} strokeOpacity={0.2} />
          <PolarAngleAxis
            dataKey="category"
            tick={({ x, y, payload, textAnchor }) => {
              const words = String(payload.value).split(' ');
              const lines: string[] = isMobile
                ? MOBILE_AXIS_LINES[String(payload.value)] ?? [String(payload.value)]
                : [];
              if (!isMobile) {
                for (let i = 0; i < words.length; i += 2) {
                  lines.push(words.slice(i, i + 2).join(' '));
                }
              }
              const labelOffset = isMobile ? 4 : 16;
              const lineHeight = isMobile ? 12 : 14;
              const angleInRad = (payload.coordinate * Math.PI) / 180;
              const shiftedX = x + Math.cos(angleInRad) * labelOffset;
              const shiftedY = y - Math.sin(angleInRad) * labelOffset;
              return (
                <g transform={`translate(${shiftedX},${shiftedY})`}>
                  {lines.map((line, index) => (
                    <text
                      key={`${line}-${index}`}
                      x={0}
                      y={(index - (lines.length - 1) / 2) * lineHeight}
                      textAnchor={textAnchor as any}
                      dominantBaseline="middle"
                      fontSize={isMobile ? 9 : 12}
                      className="text-foreground"
                    >
                      {line}
                    </text>
                  ))}
                </g>
              );
            }}
          />
          <PolarRadiusAxis
            tick={false}
            axisLine={false}
            domain={[0, data.definition.scale_max]}
          />
          <Radar dataKey="value" stroke={primary} fill={primary} fillOpacity={0.05} />
          </RadarChart>
        </ResponsiveContainer>
      </div>
      {latestResponseAt ? (
        <p className="text-center text-xs text-muted-foreground">
          Current scores use up to {data.definition.recent_response_count} recent response
          {data.definition.recent_response_count === 1 ? '' : 's'} per question
          {' · '}Most recent response:{' '}
          {new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          }).format(new Date(latestResponseAt))}
        </p>
      ) : null}
    </div>
  );
};
